import React, { Component } from "react";
import PropTypes from "prop-types";
import { Map, List } from "immutable";
import { isMobile } from "react-device-detect";
import ImmutablePropTypes from "react-immutable-proptypes";
import { connect } from "react-redux";
import { Helmet } from "react-helmet";

import { isSameCurrentVersion } from "fe-shared/constants/widget";
import ChatInput from "fe-shared/components/ChatInput";
import { selectData } from "fe-shared/utils/reducerUtils";
import { isBackgroundTransparent } from "fe-shared/utils/styleManipulator";

import {
  getCommunicationConfigDataWebchatKey,
  getCommunicationConfigDataOrigin,
  getCommunicationConfigDataIsDebuggerMode,
} from "reducers/communicationConfig";
import {
  getConfigDataPersistentMenu,
  getConfigDataImageUrl,
  getConfigDataGreetingText,
} from "reducers/config";
import { getWebviewIsFocus } from "reducers/webview";
import {
  getWebbotVisibleMessages,
  getWebbotMessagesForDebuggerMode,
  getWebbotIsTyping,
} from "reducers/webbot";
import { getQueryVariable, isIE } from "utils/index";
import { WIDGET_LINK_CLICK } from "constants/events";
import { getAppInsights } from "containers/applicationInsights/createApplicationInsights";

import { isBotxoDomain } from "../../../utils/rebranding";

// COMPONENTS
const Main = React.lazy(() => import("../../../components/App"));
const SetInitialVariables = React.lazy(() =>
  import("../../../components/SetInitialVariables")
);
const WebchatHeader = React.lazy(() =>
  import("../../../components/WebchatHeader")
);
const MessagesContainer = React.lazy(() =>
  import("../../../components/MessagesContainer")
);
const NewMessage = React.lazy(() => import("../../../components/NewMessage"));
const ShowResponseToWH = React.lazy(() =>
  import("../../../components/ShowResponseToWH")
);
const CertainlySignature = React.lazy(() =>
  import("../../../components/WebchatHeader/components/CertainlySignature")
);

const isPrevDomain = isBotxoDomain();

class Chat extends Component {
  state = {
    show: false,
    webviewUrl: null,
    windowSize: "full",
    isMaximized: false,
    isOpenedDebuggerMode: false,
    showSetInitialVariables: false,
    showResponseWH: false,
    dataResponseWH: false,
    isSpeaking: false,
  };

  componentWillMount() {
    this.setState({
      isOpenedDebuggerMode: this.getLocationQuery("isFlowTester") === "1",
    });
  }

  componentDidMount() {
    const { isPreview } = this.props;
    if (isPreview) {
      document.addEventListener("keydown", this.handleEscKey, false);
    }
  }

  componentWillUnmount() {
    const { isPreview } = this.props;
    if (isPreview) {
      document.removeEventListener("keydown", this.handleEscKey, false);
    }
  }

  handleSpeakEnd = () => {
    this.setState({ isSpeaking: false });
  };

  handleSpeakStart = () => {
    this.setState({ isSpeaking: true });
  };

  handleEscKey = event => {
    const { isPreview } = this.props;
    if (event.keyCode === 27 && isPreview) {
      this.onHideHandler();
    }
  };

  getLocationQuery = key => {
    const { location } = this.props;
    return getQueryVariable(key, location.search);
  };

  onSend = message => {
    const {
      actions: { sendMessage },
    } = this.props;
    const messageWithTrim = message && message.trim();
    if (messageWithTrim.length !== 0) {
      sendMessage({ message });
    }
  };

  onChangePM = data => {
    const type = data.get("type");
    const label = data.get("label");
    const options = data.get("options");

    const {
      actions: { persistentMenuItemClick },
      botId,
    } = this.props;
    const showIframe = type === 0;
    getAppInsights().trackEvent({
      name: WIDGET_LINK_CLICK,
      properties: {
        "Bot Deployment ID": botId,
        "Link Destination URL": options.get("url"),
        "Link Type":
          options.get("window_size") === "full"
            ? "Full widget"
            : options.get("window_size") === "tall"
            ? "Tall widget"
            : "Compact widget",
        "Link Placement": "Persistent Menu",
      },
    });
    persistentMenuItemClick(type, label, options);
    this.setState({
      webviewUrl: options.get("url"),
      show: showIframe,
      windowSize: options.get("window_size"),
    });
  };

  onHideWebview = () => {
    this.setState({
      show: false,
      windowSize: "full",
    });
  };

  onHideHandler = () => {
    const { communicationConfigDataWebchatKey } = this.props;
    const key = communicationConfigDataWebchatKey || "";

    if (window.top !== window) {
      window.top.postMessage({ message: `closeChat-${key}` }, "*");
    } else {
      window.close();
    }

    window.top.postMessage(
      {
        event: "widgetclose",
        message: `eventChat-${key}`,
        params: { from: "X" },
      },
      "*"
    );

    window.top.postMessage(
      {
        event: "onWidgetClosed",
        message: `eventChat-${key}`,
        params: { from: "X" },
      },
      "*"
    );
  };

  onMaximizeHandler = () => {
    const { communicationConfigDataWebchatKey } = this.props;
    const { isMaximized } = this.state;
    const key = communicationConfigDataWebchatKey || "";

    if (isMaximized) {
      window.top.postMessage({ message: `minimizeChat-${key}` }, "*");
    } else {
      window.top.postMessage({ message: `maximizeChat-${key}` }, "*");
    }

    this.setState({ isMaximized: !isMaximized });
  };

  onChangeWebviewHandler = ({ url, windowSize = "full", placement }) => {
    const { botId } = this.props;
    getAppInsights().trackEvent({
      name: WIDGET_LINK_CLICK,
      properties: {
        "Bot Deployment ID": botId,
        "Link Destination URL": url,
        "Link Type": windowSize === "full" ? "Full widget" : "Compact widget",
        "Link Placement": placement,
      },
    });
    this.setState({
      webviewUrl: url,
      show: true,
      windowSize,
    });
  };

  onChangeStateOfIsOpenedDebuggerMode = value => {
    const message = value ? "openDebuggerMode" : "closeDebuggerMode";
    window.top.postMessage({ message }, "*");
    this.setState({ isOpenedDebuggerMode: value });
  };

  getButtonColour = styles => {
    const version = selectData(styles, "version");
    if (isSameCurrentVersion(version)) {
      return styles.getIn(["chatView", "buttonSend", "backgroundColor"]);
    }
    return styles.getIn(["send_button", "backgroundColor"]);
  };

  setBackgroundValue = chatViewStyles => {
    const { isOpenedDebuggerMode } = this.state;
    const queryBackground = this.getLocationQuery("background");
    if (isOpenedDebuggerMode) {
      return chatViewStyles.set("backgroundColor", "#ffffff");
    }
    if (queryBackground && chatViewStyles) {
      return chatViewStyles.set("backgroundColor", queryBackground);
    }
    return chatViewStyles;
  };

  composeStylesChatView = () =>
    Map({
      backgroundColor: this.getBackgroundValue(),
    });

  showSetInitialVariables = () => {
    this.setState({ showSetInitialVariables: true });
  };

  onHideSetInitialValues = () => {
    this.setState({ showSetInitialVariables: false });
  };

  showResponseWH = (data = false) => {
    this.setState({ showResponseWH: true, dataResponseWH: data });
  };

  onHideShowResponseWH = () => {
    this.setState({ showResponseWH: false });
  };

  addOpenGraphMetaTags = () => {
    const {
      greetingText,
      botImageUrl,
      match: {
        params: { id },
      },
      styles,
    } = this.props;

    let customStyles = null;
    if (styles && styles.size > 0) {
      const stylesJS = styles.toJS();
      const { fontOption, fontCssUpload } = stylesJS.chatView;
      if (fontOption === "custom") {
        customStyles = fontCssUpload;
      }
    }

    const botName = greetingText || "Certainly bot";
    const botImage =
      botImageUrl ||
      "https://www.botxo.ai/wp-content/uploads/2020/02/Logo-color-300x126.png";
    const botDescription = "Automated with ❤ by Certainly";

    const botType = "website";
    const domain = document.location.origin;
    const botUrl = domain
      ? `${domain}/webchat/${id}/`
      : `http://certainlyfetesting.localhost:8082/webchat/${id}/`;

    return (
      <Helmet>
        {!customStyles ? (
          <link rel="preconnect" href="https://fonts.googleapis.com" />
        ) : null}
        {!customStyles ? (
          <link
            rel="stylesheet"
            href="https://fonts.googleapis.com/css2?family=Lato&family=Open+Sans&family=Lora&family=Poppins&family=PT+Sans&family=Open+Sans+Condensed&family=Mulish&family=Noto+Sans&family=Merriweather&family=Montserrat&family=Noto+Sans&family=Oswald&family=Playfair+Display&family=Raleway&family=Roboto+Condensed&family=Roboto+Slab&family=Roboto&family=Source+Sans+Pro&family=Ubuntu&display=swap"
          />
        ) : (
          <style type="text/css">{`${customStyles}`}</style>
        )}
        <meta name="title" content={botName} />
        <meta name="description" content={botDescription} />
        <meta property="og:title" content={botName} />
        <meta property="og:locale" content="en_US" />
        <meta property="og:description" content={botDescription} />
        <meta property="og:image" content={botImage} />
        <meta property="og:type" content={botType} />
        <meta property="og:url" content={botUrl} />
      </Helmet>
    );
  };

  render() {
    const {
      messages,
      isPreview,
      styles,
      persistentMenu,
      botImageUrl,
      actions: { sendMessage, setFocus, saveCVars },
      isBotTyping,
      isInputDisabled,
      isFocus,
      messagesForDebuggerMode,
      eventClickInput,
      communicationConfigDataOrigin,
      match: {
        params: { id },
      },
      resetChat,
      cvarsValue,
      isAuthenticated,
    } = this.props;
    let { greetingText } = this.props;
    const {
      show,
      webviewUrl,
      windowSize,
      isOpenedDebuggerMode,
      isMaximized,
      showSetInitialVariables,
      showResponseWH,
      dataResponseWH,
      isSpeaking,
    } = this.state;

    const greetingInQueryParams = this.getLocationQuery("greetingText");
    const botxoUnbranded = this.getLocationQuery("botxo-unbranded");
    greetingText = greetingInQueryParams || greetingText;

    const isMobileOrIE = isMobile || isIE();
    const isDebuggerMode = this.getLocationQuery("debuggerMode") === "1";
    const voiceEnabled = this.getLocationQuery("voiceEnabled") === "1";
    const speechEnabled = this.getLocationQuery("speechEnabled") === "1";
    const language = this.getLocationQuery("language");
    const autoSend = this.getLocationQuery("autoSend") === "1";
    const chatViewStyles = this.setBackgroundValue(
      selectData(styles, "chatView")
    );
    const botListId = this.getLocationQuery("botList_id");
    const inputScriptType = this.getLocationQuery("input_type") ?? "default";
    const isBgTransparent = isBackgroundTransparent(
      selectData(chatViewStyles, "backgroundColor")
    );

    const alternativeTexts = styles && selectData(styles, "alternative_texts");
    const widgetLabelList = styles && selectData(styles, "widgetLabel");
    const widgetLabel = widgetLabelList && widgetLabelList.toJS();

    return (
      <React.Suspense fallback={<div />}>
        {this.addOpenGraphMetaTags()}
        <Main {...{ chatViewStyles }}>
          {!show && (
            <WebchatHeader
              onHideHandler={this.onHideHandler}
              onMaximizeHandler={this.onMaximizeHandler}
              botxoUnbranded={botxoUnbranded}
              closeButton={
                !isBgTransparent && styles.getIn(["chatView", "buttonClose"])
              }
              maximizeButton={
                !isBgTransparent && styles.getIn(["chatView", "buttonMaxime"])
              }
              botDeploymentId={id}
              stylesPersonalized={styles.getIn(["chatMessage", "header"])}
              {...{
                chatViewStyles,
                alternativeTexts,
                widgetLabel,
                isPreview,
                isDebuggerMode,
                isOpenedDebuggerMode,
                communicationConfigDataOrigin,
              }}
            />
          )}
          <MessagesContainer
            onHideWebview={this.onHideWebview}
            onHideHandler={this.onHideHandler}
            changeWebview={this.onChangeWebviewHandler}
            openDebuggerMode={this.onChangeStateOfIsOpenedDebuggerMode}
            showResponseWH={this.showResponseWH}
            onSpeakEnd={this.handleSpeakEnd}
            onSpeakStart={this.handleSpeakStart}
            {...{
              botImageUrl,
              greetingText,
              show,
              windowSize,
              webviewUrl,
              styles,
              alternativeTexts,
              messages,
              sendMessage,
              setFocus,
              isBotTyping,
              isDebuggerMode,
              isOpenedDebuggerMode,
              messagesForDebuggerMode,
              chatViewStyles,
              isAuthenticated,
              speechEnabled,
              language,
              botListId,
              inputScriptType,
            }}
          />
          {showSetInitialVariables && (
            <SetInitialVariables
              onHide={this.onHideSetInitialValues}
              {...{ saveCVars, cvarsValue, resetChat }}
            />
          )}
          {showResponseWH && (
            <ShowResponseToWH
              onHide={this.onHideShowResponseWH}
              {...{ dataResponseWH }}
            />
          )}
          {!show && (
            <NewMessage
              openDebuggerMode={this.onChangeStateOfIsOpenedDebuggerMode}
              setInitialVariables={this.showSetInitialVariables}
              isEmptyCVars={cvarsValue.length === 0}
              {...{
                isDebuggerMode,
                isOpenedDebuggerMode,
                isBgTransparent,
                chatViewStyles,
                isMaximized,
                resetChat,
                inputScriptType,
                persistentMenu,
              }}
            >
              <ChatInput
                persistentCallback={this.onChangePM}
                onSend={this.onSend}
                onClickInput={eventClickInput}
                buttonColour={this.getButtonColour(styles)}
                isMobile={isMobileOrIE}
                fontSize={selectData(chatViewStyles, "fontSize")}
                isSpeaking={isSpeaking}
                inputScriptStyles={styles.getIn(["chatMessage", "input"])}
                // voiceEnabled={1}
                {...{
                  persistentMenu,
                  isFocus,
                  isInputDisabled,
                  language,
                  autoSend,
                  alternativeTexts,
                  inputScriptType,
                  voiceEnabled,
                }}
              />
              {!isPrevDomain && isBgTransparent && (
                <CertainlySignature
                  {...{
                    communicationConfigDataOrigin,
                    botDeploymentId: id,
                    widgetLabel,
                    isTransparent: true,
                  }}
                />
              )}
            </NewMessage>
          )}
        </Main>
      </React.Suspense>
    );
  }
}

Chat.propTypes = {
  actions: PropTypes.object.isRequired, // eslint-disable-line
  messages: ImmutablePropTypes.list.isRequired,
  styles: ImmutablePropTypes.map,
  persistentMenu: ImmutablePropTypes.list,
  botImageUrl: PropTypes.string,
  match: PropTypes.object.isRequired, // eslint-disable-line
  location: PropTypes.object, // eslint-disable-line
  greetingText: PropTypes.string,
  isBotTyping: PropTypes.bool.isRequired,
  isInputDisabled: PropTypes.bool,
  isFocus: PropTypes.bool,
  communicationConfigDataWebchatKey: PropTypes.string,
  communicationConfigDataOrigin: PropTypes.string,
  messagesForDebuggerMode: ImmutablePropTypes.list,
  eventClickInput: PropTypes.func,
  isPreview: PropTypes.bool,
  resetChat: PropTypes.func,
  saveCVars: PropTypes.func,
  cvarsValue: PropTypes.string,
  isAuthenticated: PropTypes.bool,
  botId: PropTypes.string,
};

Chat.defaultProps = {
  styles: Map(),
  persistentMenu: List(),
  botImageUrl: "",
  greetingText: "",
  isInputDisabled: false,
  isFocus: false,
  isPreview: false,
  isAuthenticated: false,
};

const mapStateToProps = (state, props) => {
  const isDebuggerMode = getCommunicationConfigDataIsDebuggerMode(state);
  let messagesForDebuggerMode = List();
  if (isDebuggerMode) {
    messagesForDebuggerMode = getWebbotMessagesForDebuggerMode(state);
  }
  return {
    persistentMenu: getConfigDataPersistentMenu(state),
    botImageUrl: getConfigDataImageUrl(state),
    messages: getWebbotVisibleMessages(state),
    isBotTyping: getWebbotIsTyping(state),
    isFocus: getWebviewIsFocus(state),
    greetingText: getConfigDataGreetingText(state),
    communicationConfigDataWebchatKey:
      getCommunicationConfigDataWebchatKey(state),
    communicationConfigDataOrigin: getCommunicationConfigDataOrigin(state),
    messagesForDebuggerMode,
    ...props,
  };
};

export default connect(mapStateToProps)(Chat);
